import React, { useEffect, useState } from "react";
import { X, Clock, FileText, Loader2, Calendar, Timer } from "lucide-react";
import { createOvertime } from "../../../api/hrm/employees/overtimes";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all hover:border-gray-400 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

const OverTimeCreateModal = ({ isOpen, onClose, onSaved }) => {
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const [loading, setLoading] = useState(false);
  const [workDate, setWorkDate] = useState("");
  const [otFrom, setOTFrom] = useState("");
  const [otTo, setOTTo] = useState("");
  const [otTime, setOTTime] = useState("");
  const [otValid, setOtValid] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      setWorkDate(now.toISOString().split("T")[0]);
      setOTFrom("");
      setOTTo("");
      setReason("");
      setOTTime("");
      setOtValid(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (otFrom && otTo) {
      const [fromHour, fromMin] = otFrom.split(":").map(Number);
      const [toHour, toMin] = otTo.split(":").map(Number);
      const fromMinutes = fromHour * 60 + fromMin;
      const toMinutes = toHour * 60 + toMin;
      if (toMinutes > fromMinutes) {
        const duration = toMinutes - fromMinutes;
        const hours = Math.floor(duration / 60);
        const mins = duration % 60;
        setOTTime(`${hours} giờ ${mins} phút`);
        setOtValid(true);
      } else {
        setOTTime("Giờ kết thúc phải sau giờ bắt đầu");
        setOtValid(false);
      }
    } else {
      setOTTime("");
      setOtValid(false);
    }
  }, [otFrom, otTo]);

  const handleSave = async () => {
    if (!user?.id || !workDate || !otFrom || !otTo || !reason.trim()) {
      showErrorToast("Vui lòng nhập đầy đủ thông tin");
      return;
    }

    const [fromHour, fromMin] = otFrom.split(":").map(Number);
    const [toHour, toMin] = otTo.split(":").map(Number);
    const fromMinutes = fromHour * 60 + fromMin;
    const toMinutes = toHour * 60 + toMin;

    if (toMinutes <= fromMinutes) {
      showErrorToast("Giờ kết thúc phải sau giờ bắt đầu");
      return;
    }

    const ot_minutes = toMinutes - fromMinutes;
    const payload = {
      employee_id: user.id,
      work_date: workDate,
      ot_from: otFrom,
      ot_to: otTo,
      ot_minutes,
      reason: reason.trim(),
    };

    try {
      setLoading(true);
      await createOvertime(payload);
      showSuccessToast("Đăng ký tăng ca thành công");
      onSaved?.();
      onClose();
    } catch (error) {
      if (error.response?.status === 409) {
        showErrorToast("Bạn đã đăng ký tăng ca cho ngày này rồi.");
      } else {
        showErrorToast("Đăng ký thất bại");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "—";
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Clock className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      Đăng ký <span className="text-red-700">tăng ca</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Yêu cầu làm thêm giờ trong ngày
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Thời gian tăng ca
                  </h3>

                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-slate-400" />
                      <span className={`${labelClass} !ml-0`}>Ngày làm việc</span>
                    </div>
                    <span className="text-sm font-black text-gray-800">
                      {formatDate(workDate)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Clock size={12} className="text-red-500" />
                        Giờ bắt đầu <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={otFrom}
                        onChange={(e) => setOTFrom(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Clock size={12} className="text-red-500" />
                        Giờ kết thúc <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={otTo}
                        onChange={(e) => setOTTo(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {otTime ? (
                    <div
                      className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 ${
                        otValid
                          ? "border-emerald-100 bg-emerald-50 text-emerald-800"
                          : "border-rose-100 bg-rose-50 text-rose-700"
                      }`}
                    >
                      <Timer size={14} />
                      <span className="text-[11px] font-bold uppercase tracking-wider">
                        {otValid ? "Thời lượng" : "Lỗi"}
                      </span>
                      <span className="text-sm font-black">{otTime}</span>
                    </div>
                  ) : null}
                </section>

                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Lý do
                  </h3>
                  <div>
                    <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                      <FileText size={12} className="text-red-500" />
                      Lý do tăng ca <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="VD: Hoàn thiện báo cáo, hỗ trợ sản xuất..."
                      maxLength={250}
                      className={`${inputClass} resize-none`}
                    />
                    <p
                      className={`mt-1 text-right text-[11px] ${
                        reason.length > 200
                          ? "font-bold text-red-500"
                          : "text-gray-400"
                      }`}
                    >
                      {reason.length}/250
                    </p>
                  </div>
                </section>
              </div>

              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    "Gửi đăng ký"
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OverTimeCreateModal;
